import { useCallback } from "react";
import { toast } from "react-toastify";
import type { useFacetExecuteEtl, useGetFacet } from "../../tars-hooks";
import { facetEtlExecuteParams } from "./facet-etl-execute-helpers";
import {
    buildNormalizeKeyProbeEtlDoc,
    type NormalizeKeyDataSourceHint,
    resolveNormalizeKeyValueOptions,
    resolveNormalizeKeyValueOptionsKeyed,
    resolveProbePivotRowLabels,
} from "./facet-normalize-key-helpers";

type GetFacetMutation = ReturnType<typeof useGetFacet>;
type ExecuteEtlMutation = ReturnType<typeof useFacetExecuteEtl>;

interface UseNormalizeKeyFacetRefreshParams {
    facetId: number | null;
    notebookId: number;
    dataSourceHint: NormalizeKeyDataSourceHint;
    getFacet: GetFacetMutation;
    executeEtl: ExecuteEtlMutation;
    onKeyFetchStart: (normalizeKey: string) => void;
    onKeyOptionsLoaded: (normalizeKey: string, options: string[]) => void;
    onOptionsRefresh: () => void;
}

function optionsFromExecuteResponse(execResp: unknown, key: string): string[] {
    const fromHeaders = resolveNormalizeKeyValueOptions(execResp, key, { keyRequestedFromApi: true });
    if (fromHeaders.length > 0) return fromHeaders;
    const fromKeyed = resolveNormalizeKeyValueOptionsKeyed(execResp, key);
    if (fromKeyed.length > 0) return fromKeyed;
    return resolveProbePivotRowLabels(execResp);
}

export function useNormalizeKeyFacetRefresh({
    facetId,
    notebookId,
    dataSourceHint,
    getFacet,
    executeEtl,
    onKeyFetchStart,
    onKeyOptionsLoaded,
    onOptionsRefresh,
}: UseNormalizeKeyFacetRefreshParams) {
    const finish = useCallback(
        (key: string, options: string[]) => {
            onKeyOptionsLoaded(key, options);
            onOptionsRefresh();
            if (options.length === 0) {
                toast.error(
                    "No values found for this key. Copy the full key from the sidebar list."
                );
            }
        },
        [onKeyOptionsLoaded, onOptionsRefresh]
    );

    const runFacetGet = useCallback(
        (key: string) => {
            if (!facetId) {
                finish(key, []);
                return;
            }
            getFacet.mutate(
                { facet_id: facetId, key },
                {
                    onSuccess: (resp) => {
                        let options = resolveNormalizeKeyValueOptions(resp, key, {
                            keyRequestedFromApi: true,
                        });
                        if (!options.length) {
                            options = resolveNormalizeKeyValueOptionsKeyed(resp, key);
                        }
                        finish(key, options);
                    },
                    onError: () => finish(key, []),
                }
            );
        },
        [facetId, getFacet, finish]
    );

    const runProbePivot = useCallback(
        (json: string, key: string) => {
            let parsed: Record<string, unknown>;
            try {
                parsed = JSON.parse(json) as Record<string, unknown>;
            } catch {
                finish(key, []);
                return;
            }

            const probeDoc = buildNormalizeKeyProbeEtlDoc(parsed, key, dataSourceHint);
            if (!probeDoc) {
                toast.error("Select a data repo with an ES index in the sidebar.");
                runFacetGet(key);
                return;
            }

            const execParams = facetEtlExecuteParams(probeDoc);
            if (!execParams) {
                finish(key, []);
                return;
            }

            executeEtl.mutate(
                {
                    etl_json: execParams.etl_json,
                    decimal_places: execParams.decimal_places,
                    ...(Number.isFinite(notebookId) && notebookId > 0 ? { notebook_id: notebookId } : {}),
                },
                {
                    onSuccess: (execResp) => {
                        const options = optionsFromExecuteResponse(execResp, key);
                        if (options.length > 0) finish(key, options);
                        else runFacetGet(key);
                    },
                    onError: () => runFacetGet(key),
                }
            );
        },
        [dataSourceHint, executeEtl, finish, notebookId, runFacetGet]
    );

    return useCallback(
        (json: string, normalizeKey: string) => {
            const key = normalizeKey.trim();
            if (!key) return;
            onKeyFetchStart(key);
            runProbePivot(json, key);
        },
        [onKeyFetchStart, runProbePivot]
    );
}
